<template>
  <div
    class="flex h-full items-center justify-center gap-1 px-1 py-1.5"
    data-cy="schedule-column-header"
  >
    <UnassignedAvatar v-if="isUnassigned" size-class="h-5 w-5" />
    <Avatar
      v-else
      :alt="fullName"
      :src="technician?.avatar?.url"
      :status="technician?.is_clocked_in ? 'online' : 'offline'"
      :initials="initials"
      size-class="w-5 h-5"
      text-class="text-[9px]"
      class="shrink-0"
    />
    <!-- Not `truncate`: this project's build of it carries `width: 100%`, which
         stretches the name to the column edge and strands the avatar on the far
         left instead of centring the pair. These three do the clipping without
         claiming the width. -->
    <span
      class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold text-gray-900"
      :title="fullName"
    >
      {{ label }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import Avatar from "@/components/Seranova/Indicator/Avatar.vue";
import { getInitials } from "@/mixins";
import {
  ResourceAPIResponse,
  UNASSIGNED_RESOURCE_ID
} from "@/typings/scheduler";
import UnassignedAvatar from "./UnassignedAvatar.vue";

/**
 * The header above one phone column: whose schedule that column is drawing.
 *
 * Avatar and name sit on one line rather than stacked. Stacking them spent the
 * header's height twice over on the same question; side by side keeps both the
 * face and the name a column is identified by, and the name is what gives way
 * first when the column narrows.
 *
 * Rendered through `getComponentHtml`, which mounts this as its own Vue app
 * with nothing but the vuex store, so a `useEventCalendar()` call here would
 * inject nothing and rebuild the whole hook — once per column, per repaint.
 * The hook's `resources` is store-derived, so reading the store directly is
 * the same answer without the reconstruction. ResourceContent reads it the
 * same way, for the same reason.
 */
const props = defineProps<{ resource: { id: number; name?: string } }>();

const store = useStore();

const isUnassigned = computed(
  () => props.resource.id === UNASSIGNED_RESOURCE_ID
);

// Mobiscroll builds its resources with id, colour and name alone, so the avatar
// and the clocked-in dot are looked back up on the roster the store holds.
const technician = computed<ResourceAPIResponse | undefined>(() =>
  store.state.timeline?.resources?.find(
    (resource: ResourceAPIResponse) => resource.id === props.resource.id
  )
);

// The board's own name for the row is the fallback: a technician dropped from
// the roster mid-render still has a column to label.
const fullName = computed(
  () => technician.value?.full_name || props.resource.name || ""
);

const initials = computed(() => getInitials({ full_name: fullName.value }));

// First name only: a column is a third of a phone screen, and a full name
// truncates there to something that identifies nobody.
const label = computed(() =>
  isUnassigned.value ? "Unassigned" : fullName.value.trim().split(" ")[0]
);
</script>
